import React from "react";
import {
  ButtonComp,
  LoanTypeDetails,
  MoneyInputComp,
  SelectComp2,
  TextInputComp,
} from "../../../components";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { AiOutlineClose } from "react-icons/ai";
import { useEnums } from "../../../hooks/useEnums";
import {
  useGetMemberLoanTypesQuery,
  useMemberLoanCalculatorMutation,
} from "../../../store/member/member-loan";
import { storage } from "../../../utils/storage";
import { useNavigate } from "react-router-dom";

const LoanCalculatorModal = ({ setModalPage }) => {
  const enums = useEnums();
  const navigate = useNavigate();
  const { data: loanTypes, isLoading: isLoadingLoanTypes } =
    useGetMemberLoanTypesQuery();

  const formattedLoanTypes = loanTypes?.data?.configs
    ?.filter((item) => item && item.id)
    ?.map((item) => ({
      label: item.name,
      value: item.id,
      id: item.id,
      max_guarantor: item.max_guarantor,
      min_guarantor: item.min_guarantor,
      min_loan_amount: item.min_loan_amount,
      max_loan_amount: item.max_loan_amount,
      rate: item.rate,
      interest_calculation_mode: item.interest_calculation_mode,
      loan_fee: item.loan_fee,
      ...item,
    }));

  const [calculateLoan, { isLoading }] = useMemberLoanCalculatorMutation();

  const handleLoanCalculation = async (values, { resetForm }) => {
    // Server derives repayment_plan from duration_type (matched units)
    const payload = {
      loan_type_id: values.loan_type_id,
      amount: values.amount,
      duration: values.duration,
      duration_type: values.duration_type,
    };
    const response = await calculateLoan(payload);
    const { data, error } = response;
    if (error) {
      return toast.error(error?.data?.message || "An error occured");
    } else if (data?.status === false) {
      return toast.error(data?.message || "An error occured");
    } else {
      toast.success(data?.message || "Successful");
      storage["localStorage"].set("loan_calculator_result", data?.data);
      setModalPage();
      resetForm();
      navigate("/member/loan_management/calculation");
    }
  };

  return (
    <div className="relative px-4 pb-4">
      <Formik
        initialValues={{
          loan_type_id: null,
          duration_type: "",
          amount: null,
          duration: null,
        }}
        validationSchema={Yup.object({
          loan_type_id: Yup.string().required("Required"),
          amount: Yup.string().required("Required"),
          duration: Yup.string().required("Required"),
          duration_type: Yup.string().required("Required"),
        })}
        onSubmit={handleLoanCalculation}
      >
        {({ values, errors, touched, setFieldValue }) => {
          const selectedLoanType =
            formattedLoanTypes?.find(
              (item) => String(item.value) === String(values.loan_type_id)
            ) || null;

          return (
            <Form className=" space-y-4">
              <AiOutlineClose
                onClick={() => setModalPage()}
                className="absolute top-4 left-7"
              />
              <h4 className="border-b fw-bold py-2 mb-4 text-[1.5rem] !text-primary text-center w-full ">
                Loan Calculator
              </h4>
              <SelectComp2
                LabelClassName={"fw-2"}
                placeholder={"Select loan type"}
                labelText={"Loan type"}
                arrayComp={formattedLoanTypes}
                isLoading={isLoadingLoanTypes}
                selectedOption={values.loan_type_id}
                setSelectedOption={(selectedOption) => {
                  setFieldValue("loan_type_id", selectedOption.value);
                }}
                errors={
                  touched.loan_type_id && errors.loan_type_id
                    ? [errors.loan_type_id]
                    : []
                }
              />
              {selectedLoanType && (
                <LoanTypeDetails
                  loanType={selectedLoanType}
                  variant="calculator"
                  durationType={values.duration_type}
                />
              )}
              <MoneyInputComp
                LabelText={"Loan amount"}
                borderColor="#A1A0A0"
                LabelClassName={"fw-2"}
                placeholder={"Enter amount"}
                descClassName="text-danger"
                name={"amount"}
              />

              <TextInputComp
                LabelText={"Duration"}
                borderColor="#A1A0A0"
                LabelClassName={"fw-2"}
                placeholder={"0"}
                name="duration"
                type="text"
              />
              <SelectComp2
                LabelClassName={"fw-2"}
                labelText={"Duration type"}
                arrayComp={enums["DurationType"] ?? []}
                selectedOption={values.duration_type}
                setSelectedOption={(selectedOption) =>
                  setFieldValue("duration_type", selectedOption.value)
                }
                errors={
                  touched.duration_type && errors.duration_type
                    ? [errors.duration_type]
                    : []
                }
              />
              <p className="text-muted small mb-0">
                Repayments follow the duration type (e.g. months → monthly).
              </p>

              <div className="row justify-content-around pt-8">
                <ButtonComp
                  loading={isLoading}
                  type="submit"
                  btnText={"Calculate loan interest"}
                  ButtonInlineStyle={{
                    backgroundColor: "var(--color-primary)",
                  }}
                  btnClassName={"border-0 text-white  w-100 py-2 rounded"}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default LoanCalculatorModal;
